import React from 'react';
import HomeModalShell from '../../home/components/HomeModalShell';
import {ProfileApiFacebookPageOption} from '../../../../services/profileApi';
import styles from '../Profile.module.css';

interface ProfileFacebookPagePickerModalProps {
  isOpen: boolean;
  pages: ProfileApiFacebookPageOption[];
  loading: boolean;
  submittingPageId: string | null;
  error: string | null;
  onClose: () => void;
  onSelect: (pageId: string) => void;
}

const ProfileFacebookPagePickerModal: React.FC<ProfileFacebookPagePickerModalProps> = ({
  isOpen,
  pages,
  loading,
  submittingPageId,
  error,
  onClose,
  onSelect,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <HomeModalShell
      backdropClassName={styles.editorModalBackdrop}
      panelClassName={styles.socialModalCard}
      ariaLabelledBy="profile-facebook-page-picker-title"
      ariaDescribedBy="profile-facebook-page-picker-description"
      onClose={onClose}
      closeDisabled={loading || Boolean(submittingPageId)}
    >
      <div className={styles.editorModalHeader}>
        <div>
          <h2 id="profile-facebook-page-picker-title" className={styles.editorModalTitle}>
            Choose a Facebook Page
          </h2>
          <p id="profile-facebook-page-picker-description" className={styles.editorModalDescription}>
            Select the Facebook Page you want to connect to your profile. After you pick one, the card will return to
            the normal Open Link state.
          </p>
        </div>
        <button
          type="button"
          className={styles.editorModalClose}
          onClick={onClose}
          disabled={loading || Boolean(submittingPageId)}
          aria-label="Close Facebook Page picker"
        >
          ×
        </button>
      </div>

      {error ? (
        <div className={`${styles.editorMessage} ${styles.editorMessageError}`}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className={styles.facebookPagePickerEmpty}>Loading your Facebook Pages...</div>
      ) : pages.length > 0 ? (
        <div className={styles.facebookPagePickerList}>
          {pages.map((page) => {
            const isSubmitting = submittingPageId === page.id;

            return (
              <div key={page.id} className={styles.facebookPagePickerOption}>
                <div className={styles.facebookPagePickerIdentity}>
                  {page.avatarUrl ? (
                    <img
                      src={page.avatarUrl}
                      alt=""
                      className={styles.facebookPagePickerAvatar}
                    />
                  ) : (
                    <div className={styles.facebookPagePickerAvatarFallback}>
                      {page.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className={styles.facebookPagePickerText}>
                    <div className={styles.facebookPagePickerName}>{page.name}</div>
                    <div className={styles.facebookPagePickerLink}>
                      {page.link ? page.link.replace(/^https?:\/\//, '') : `facebook.com/${page.id}`}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className={`${styles.editorModalButton} ${styles.editorModalButtonPrimary}`}
                  onClick={() => onSelect(page.id)}
                  disabled={Boolean(submittingPageId)}
                >
                  {isSubmitting ? 'Connecting...' : 'Connect Page'}
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.facebookPagePickerEmpty}>
          No Facebook Pages are ready to connect for this session.
        </div>
      )}
    </HomeModalShell>
  );
};

export default React.memo(ProfileFacebookPagePickerModal);
